/**
 * Pure helpers for /billing/school (Plan 14).
 *
 * Framework-free so the unit suite at
 * `apps/ordo/client/test/components/school-ar-helpers.test.ts` can
 * exercise the formatters + filter composition without DOM.
 */

import type {
  ArAgingBucket,
  InvoicePaymentState,
  MealPlanInvoiceStatus,
  SchoolARArAging,
  SchoolARCheckPaymentSummary,
  SchoolARInvoiceRow,
} from '@/api/billing-school'

/**
 * Format a non-negative cents amount as a localized currency string.
 * Currency code comes from the page payload (one currency per view in
 * v1; aging recomputes when the school filter narrows).
 */
export function formatCents(amountCents: number, currency: string): string {
  const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency })
  return formatter.format(amountCents / 100)
}

/**
 * `0.0%`, `7.75%` — the drawer's `Taxes (X%)` row, mirroring the summary
 * PDF's `summaryTaxRate` in `@ordo/schema`. The rate is in the shared
 * 0.001% storage unit (`TAX_RATE_STORAGE_DENOMINATOR` in @ordo/money, kept
 * inline like `lib/school-orders/tax.ts`). Always at least one decimal,
 * never rounded to one: 7.75% shown as 7.8% is a wrong number on a tax row.
 */
export function formatTaxRate(taxRateBps: number | null): string {
  const percent = taxRateBps === null ? 0 : (taxRateBps / 100000) * 100
  const text = percent.toFixed(3).replace(/0+$/, '')
  return `${text.endsWith('.') ? `${text}0` : text}%`
}

/**
 * Human-readable label for each aging bucket key.
 */
export function bucketLabel(bucket: ArAgingBucket): string {
  switch (bucket) {
    case 'current':
      return 'Current'
    case 'days0to30':
      return '0–30 days'
    case 'days31to60':
      return '31–60 days'
    case 'days60Plus':
      return '60+ days'
  }
}

/**
 * Status chip label for an invoice row. Maps Prisma's
 * `MealPlanInvoiceStatus` enum onto user-facing copy.
 */
export function invoiceStatusLabel(status: MealPlanInvoiceStatus): string {
  switch (status) {
    case 'DRAFT':
      return 'Draft'
    case 'PENDING_REVIEW':
      return 'In review'
    case 'APPROVED':
      return 'Approved'
    case 'SCHEDULED':
      return 'Scheduled'
    case 'ISSUED':
      return 'Issued'
    case 'COLLECTED':
      return 'Paid'
    case 'PAYMENT_FAILED':
      return 'Payment failed'
    case 'VOIDED':
      return 'Voided'
  }
}

/**
 * Tone for the status chip.
 */
export function invoiceStatusTone(status: MealPlanInvoiceStatus): 'default' | 'success' | 'destructive' | 'secondary' {
  switch (status) {
    case 'COLLECTED':
      return 'success'
    case 'PAYMENT_FAILED':
      return 'destructive'
    case 'VOIDED':
      return 'secondary'
    case 'SCHEDULED':
    case 'APPROVED':
    case 'PENDING_REVIEW':
    case 'DRAFT':
      return 'secondary'
    case 'ISSUED':
      return 'default'
  }
}

/**
 * Mirrors the server's `isAgingEligible` (`server/src/managers/school-ar.ts`):
 * only an invoice the school has been billed for and has not settled is money
 * owed. The aging cards, the header total, the Outstanding column and the pay
 * buttons all key off this one rule, so they cannot disagree.
 */
export function isAgingEligible(status: MealPlanInvoiceStatus): boolean {
  return status === 'ISSUED' || status === 'PAYMENT_FAILED'
}

/**
 * What the school still owes on one invoice: the unpaid remainder of a billed
 * invoice, and nothing for any other status. A voided invoice owes nothing, and
 * one not issued yet (draft, in review, approved, scheduled) is not a bill the
 * school can pay, so neither may show a balance the cards do not count.
 */
export function outstandingCents(row: Pick<SchoolARInvoiceRow, 'status' | 'totalCents' | 'paidCents'>): number {
  return isAgingEligible(row.status) ? Math.max(0, row.totalCents - row.paidCents) : 0
}

/**
 * Total cents across the 4 aging buckets — drives the "Total outstanding"
 * footer on the cards strip.
 */
export function totalOutstandingCents(aging: SchoolARArAging): number {
  return aging.current + aging.days0to30 + aging.days31to60 + aging.days60Plus
}

/**
 * Sum aging client-side from a list of invoice rows. Used when the
 * school filter narrows the view — instead of re-fetching, we
 * re-aggregate the rows the page already has. (Matches the server-side
 * `computeAgingFromInvoices` semantics: only ISSUED + PAYMENT_FAILED
 * rows count, remaining = total - paid.)
 */
export function computeAgingFromRows(rows: readonly SchoolARInvoiceRow[]): SchoolARArAging {
  const totals = { current: 0, days0to30: 0, days31to60: 0, days60Plus: 0 }
  const counts = { current: 0, days0to30: 0, days31to60: 0, days60Plus: 0 }
  let currency: string = 'USD'
  for (const r of rows) {
    const remaining = outstandingCents(r)
    if (remaining === 0) {
      continue
    }
    totals[r.bucket] += remaining
    counts[r.bucket] += 1
    currency = r.currency
  }
  return { ...totals, counts, currency }
}

/**
 * Compose the table filter: school-location narrowing AND bucket
 * narrowing applied in sequence. Returns the filtered rows.
 *
 * When both filters are unset, returns the input unchanged.
 */
export function applyTableFilters(
  rows: readonly SchoolARInvoiceRow[],
  filter: { schoolLocationIds: readonly string[] | null; bucket: ArAgingBucket | null },
): SchoolARInvoiceRow[] {
  let out: readonly SchoolARInvoiceRow[] = rows
  if (filter.schoolLocationIds && filter.schoolLocationIds.length > 0) {
    const set = new Set(filter.schoolLocationIds)
    out = out.filter((r) => r.schoolLocationId !== null && set.has(r.schoolLocationId))
  }
  if (filter.bucket) {
    // Only rows the clicked card counts — a voided or not-yet-issued invoice
    // dated into this bucket is not part of its total.
    out = out.filter((r) => r.bucket === filter.bucket && outstandingCents(r) > 0)
  }
  return out.slice()
}

/**
 * Format an ISO date string for display in the table ("May 25, 2026").
 */
export function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return iso
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * Label for the derived payment state. Distinct from
 * `invoiceStatusLabel`, which describes the INVOICE; this describes the
 * MONEY, which is what a school business manager is actually scanning
 * the table for.
 */
export function paymentStateLabel(state: InvoicePaymentState): string {
  switch (state) {
    case 'VOIDED':
      return 'Voided'
    case 'PAID':
      return 'Paid'
    case 'PAID_BY_CHECK':
      return 'Paid'
    case 'CHECK_SUBMITTED':
      return 'Payment submitted'
    case 'PAYMENT_FAILED':
      return 'Payment failed'
    case 'PAST_DUE':
      return 'Past due'
    case 'DUE':
      return 'Due'
    case 'NOT_YET_ISSUED':
      return 'Not yet issued'
  }
}

export function paymentStateTone(state: InvoicePaymentState): 'default' | 'success' | 'destructive' | 'secondary' {
  switch (state) {
    case 'PAID':
    case 'PAID_BY_CHECK':
      return 'success'
    case 'PAYMENT_FAILED':
    case 'PAST_DUE':
      return 'destructive'
    case 'VOIDED':
    case 'NOT_YET_ISSUED':
      return 'secondary'
    case 'CHECK_SUBMITTED':
    case 'DUE':
      return 'default'
  }
}

/** Human label for an offline payment method. */
export function offlineMethodLabel(method: string): string {
  switch (method) {
    case 'ACH':
      return 'ACH'
    case 'WIRE':
      return 'Wire'
    case 'CASH':
      return 'Cash'
    case 'OTHER':
      return 'Payment'
    default:
      return 'Check'
  }
}

/**
 * The secondary line under a payment badge: which instrument, its
 * reference, and the date that matters for the current state (mailed
 * while in flight, received once confirmed).
 *
 * Returns null when there is nothing more to say than the badge already
 * says — an empty caption is worse than none.
 */
export function paymentStateDetail(row: {
  paymentState: InvoicePaymentState
  checkPayment: SchoolARCheckPaymentSummary | null
}): string | null {
  const payment = row.checkPayment
  if (!payment) {
    return null
  }
  const label = offlineMethodLabel(payment.method)
  const reference =
    payment.method === 'CHECK' ? (payment.checkNumber ? `#${payment.checkNumber}` : '') : (payment.depositRef ?? '')
  const head = [label, reference].filter(Boolean).join(' ')

  if (row.paymentState === 'CHECK_SUBMITTED') {
    return `${head}, sent ${formatDate(payment.mailedAt)}`
  }
  if (row.paymentState === 'PAID_BY_CHECK' && payment.receivedAt) {
    return `${head}, received ${formatDate(payment.receivedAt)}`
  }
  return null
}

/**
 * Scope for the invoice card heading. A campus name is only truthful when no
 * row is a district invoice: a Campus Admin's district rows bill every
 * campus, and naming their campus is how "Invoices · Hillside Grade School"
 * came to sit over New Hyde Park's district bills (PROD-1467). Those name the
 * district instead and say why. Pass the rows the school chip keeps, before
 * the bucket filter, so a bucket click cannot flip the heading while a
 * campus chip still can.
 */
export function invoiceHeadingScope(input: {
  rows: readonly Pick<SchoolARInvoiceRow, 'districtInvoiceForCampusAdmin' | 'organizationName'>[]
  scopeLabel: string | null
}): { label: string | null; districtNote: string | null } {
  const district = input.rows.find((row) => row.districtInvoiceForCampusAdmin)?.organizationName
  if (district === undefined) {
    return { label: input.scopeLabel, districtNote: null }
  }
  const label = district || 'District'
  const campus = input.scopeLabel && input.scopeLabel !== label ? `, not only ${input.scopeLabel}` : ''
  return {
    label,
    // ponytail: a view mixing campus-owned and district invoices still heads
    // as the district. Prod has no campus-owned ORGANIZATION accounts today;
    // name both once one exists.
    districtNote: `District invoices cover every campus in the district${campus}. Only district-wide admins can open them or download their documents.`,
  }
}

/**
 * Which view the school Billing tab renders — driven by the resolved
 * ORGANIZATION account's billing mode:
 *
 *   PREPAID          → 'balance'  (balance load-up management)
 *   INVOICED / null  → 'invoices' (the AR ledger; also the safe default
 *                       while the account read hasn't resolved or the
 *                       school has no account yet)
 */
export function schoolBillingView(
  account: { billingMode: 'PREPAID' | 'INVOICED' | null } | null,
): 'balance' | 'invoices' {
  return account?.billingMode === 'PREPAID' ? 'balance' : 'invoices'
}
